/**
 * Chicago Skyline · Silhouette
 *
 * A clean SVG silhouette of the Chicago skyline — Willis Tower, John Hancock
 * Center, Aon Center, Trump Tower, Two Prudential Plaza, and the rest of the
 * downtown / lake-front cluster.
 *
 * Used at the foot of "The Bridge Model Story" section as a quiet visual
 * signature of where the methodology was forged. Themable via props to match
 * the parent brand palette.
 */
export default function ChicagoSkyline({
  height = 90,
  className = "",
  fill = "#0A192F",
  accent = "#D4AF37",
  glow = true,
  "data-testid": testId = "chicago-skyline",
  title = "Chicago Skyline · Where The Bridge Model was forged",
}) {
  // Stroke each building as a single filled rect/polygon set.
  // Designed at 1400 wide × 200 tall; scales by the height prop.
  return (
    <svg
      viewBox="0 0 1400 200"
      preserveAspectRatio="xMidYMax meet"
      height={height}
      width="100%"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={title}
      className={className}
      data-testid={testId}
    >
      <title>{title}</title>

      {/* Faint horizon glow (sun/lake-light) — optional */}
      {glow && (
        <defs>
          <radialGradient id="cs-glow" cx="50%" cy="100%" r="55%" fx="50%" fy="100%">
            <stop offset="0%" stopColor={accent} stopOpacity="0.18" />
            <stop offset="55%" stopColor={accent} stopOpacity="0.04" />
            <stop offset="100%" stopColor={accent} stopOpacity="0" />
          </radialGradient>
        </defs>
      )}
      {glow && (
        <rect x="0" y="80" width="1400" height="120" fill="url(#cs-glow)" />
      )}

      {/*
        SKYLINE — left to right, west to east-ish
        The shape is a single union of buildings, drawn as polygons
        so antennas / setbacks / signature crowns are honored.
       */}
      <g fill={fill}>
        {/* Far-west low-rise cluster */}
        <rect x="0" y="178" width="60" height="22" />
        <rect x="55" y="166" width="38" height="34" />
        <rect x="88" y="155" width="44" height="45" />
        <rect x="127" y="170" width="22" height="30" />

        {/* Mid-rise cluster */}
        <rect x="145" y="138" width="58" height="62" />
        <rect x="195" y="150" width="30" height="50" />
        <rect x="220" y="125" width="48" height="75" />

        {/* AON Center — tall slim white-clad rectangle (we draw as silhouette) */}
        <rect x="270" y="60" width="46" height="140" />
        {/* small AON antenna */}
        <rect x="290" y="50" width="6" height="10" />

        {/* Two Prudential Plaza — pyramid crown */}
        <polygon points="320,90 358,90 358,200 320,200" />
        <polygon points="320,90 339,72 358,90" />
        <rect x="335" y="60" width="8" height="14" />

        {/* Mid-rises */}
        <rect x="362" y="120" width="34" height="80" />
        <rect x="398" y="108" width="40" height="92" />
        <rect x="442" y="135" width="26" height="65" />

        {/* WILLIS TOWER (Sears) — the iconic stepped silhouette with twin antennas */}
        {/* base */}
        <rect x="478" y="80" width="78" height="120" />
        {/* upper setback */}
        <rect x="490" y="48" width="54" height="38" />
        {/* twin antennas */}
        <rect x="500" y="20" width="6" height="30" />
        <rect x="528" y="14" width="6" height="36" />

        {/* Slim mid-rises right of Willis */}
        <rect x="560" y="130" width="32" height="70" />
        <rect x="594" y="115" width="38" height="85" />
        <rect x="636" y="140" width="26" height="60" />

        {/* 311 South Wacker — crowned tower */}
        <rect x="664" y="100" width="40" height="100" />
        <polygon points="664,100 684,82 704,100" />
        <rect x="681" y="72" width="6" height="12" />

        {/* Chase Tower / 875 N Mich support cluster */}
        <rect x="710" y="125" width="34" height="75" />
        <rect x="746" y="105" width="46" height="95" />
        <rect x="794" y="135" width="28" height="65" />

        {/* TRUMP TOWER — stepped slim profile with spire */}
        <rect x="826" y="65" width="44" height="135" />
        <rect x="834" y="40" width="28" height="28" />
        <rect x="844" y="20" width="8" height="22" />

        {/* River North / Marina City circular silhouettes (round corncob crowns) */}
        <rect x="876" y="120" width="30" height="80" />
        <ellipse cx="891" cy="120" rx="15" ry="6" />
        <rect x="912" y="120" width="30" height="80" />
        <ellipse cx="927" cy="120" rx="15" ry="6" />

        {/* Mid-rise gap */}
        <rect x="948" y="140" width="34" height="60" />
        <rect x="984" y="120" width="44" height="80" />

        {/* JOHN HANCOCK CENTER — twin antennas + tapered profile */}
        <polygon points="1034,200 1034,50 1064,30 1094,50 1094,200" />
        {/* twin antennas */}
        <rect x="1054" y="2" width="6" height="32" />
        <rect x="1068" y="10" width="6" height="24" />

        {/* Park Tower / 900 N Mich cluster */}
        <rect x="1100" y="95" width="38" height="105" />
        <polygon points="1100,95 1119,78 1138,95" />

        {/* Mid-rises descending into east side */}
        <rect x="1142" y="130" width="30" height="70" />
        <rect x="1174" y="115" width="40" height="85" />
        <rect x="1216" y="138" width="28" height="62" />
        <rect x="1246" y="155" width="34" height="45" />
        <rect x="1282" y="148" width="40" height="52" />
        <rect x="1324" y="168" width="32" height="32" />
        <rect x="1358" y="176" width="42" height="24" />
      </g>

      {/* Lakefront / horizon ground line */}
      <rect x="0" y="198" width="1400" height="2" fill={fill} opacity="0.6" />
    </svg>
  );
}
